import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import Loading from './components/Loading';
import useAdminData from './useAdminData';
import { formatDateTime, formatMoney } from './format';
import { SALE_DATE_FIELDS } from './saleDates';

const STATUS_VARIANTS = { open: 'success', closed: 'danger', maintenance: 'warning' };

const DATE_ROWS = SALE_DATE_FIELDS.map(field => [field.label, field.name]);

function Stat({ label, value, detail, to }) {
  return (
    <Col xs={12} sm={6} lg={3} className="mb-3">
      <Card className="h-100">
        <Card.Body>
          <Card.Subtitle className="text-muted mb-2">{label}</Card.Subtitle>
          <Card.Title as="div" className="h3 mb-1">{value}</Card.Title>
          {detail && <Card.Text className="small text-muted mb-0">{detail}</Card.Text>}
          {to && <Link to={to} className="small stretched-link">View</Link>}
        </Card.Body>
      </Card>
    </Col>
  );
}

export default function AdminDashboard() {
  const { data, error, loading, waking, reload } = useAdminData('/admin/dashboard/summary');

  if (loading && !data) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return <Alert variant="danger">{error} <Button variant="link" onClick={reload}>Try again</Button></Alert>;
  }

  const { consignmentStatus, dates } = data;
  const statusUnknown = consignmentStatus === 'unknown';
  const missingDates = DATE_ROWS.filter(([, key]) => !dates[key]).map(([label]) => label);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0 mr-3">Dashboard</h2>
        <Badge variant={STATUS_VARIANTS[consignmentStatus] || 'secondary'} className="p-2">
          {statusUnknown ? 'Consignment status unknown' : `Consignment ${consignmentStatus}`}
        </Badge>
        <Link to="/admin/settings" className="ml-2 small">Change</Link>
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {statusUnknown && (
        <Alert variant="danger">
          The consignment status setting can't be read. Consigners are let in as if the sale were
          open until it is fixed. Check the <code>consignment-status</code> parameter (see the README).
        </Alert>
      )}
      {dates.isFallback && (
        <Alert variant="danger">
          The sale date setting can't be read, so item numbers may use the wrong year. Check the
          <code> date-configuration</code> parameter (see the README) before consigners add items.
        </Alert>
      )}
      {!dates.isFallback && missingDates.length > 0 && (
        <Alert variant="warning">Not set yet: {missingDates.join(', ')}.</Alert>
      )}

      <Row>
        <Stat
          label="Consigners"
          value={data.consignerCount}
          detail={`${data.vendorCount} vendor${data.vendorCount === 1 ? '' : 's'}`}
          to="/admin/consigners"
        />
        <Stat
          label="Consigners with no items"
          value={data.emptyConsignerCount}
          detail="Signed up but haven't added anything"
          to="/admin/consigners?show=empty"
        />
        <Stat
          label="Items"
          value={data.itemCount}
          to="/admin/items"
          detail={`${data.discountEligibleCount} discount eligible` +
            (data.unitCount !== data.itemCount ? ` · ${data.unitCount} counting quantities` : '')}
        />
        <Stat label="Total asking value" value={formatMoney(data.totalValue)} detail="Price × quantity" />
      </Row>

      <Card>
        <Card.Header className="d-flex">
          {dates.currentYear} sale dates (Eastern time)
          <Link to="/admin/settings" className="ml-auto small">Edit</Link>
        </Card.Header>
        <Table size="sm" className="mb-0">
          <tbody>
            {DATE_ROWS.map(([label, key]) => (
              <tr key={key}>
                <th scope="row" className="font-weight-normal text-muted" style={{ width: '40%' }}>{label}</th>
                <td>{formatDateTime(dates[key])}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
